import { useEffect, useRef, useState } from 'react';

/** Below this, a load finishes before anybody would notice a loader. */
const SHOW_AFTER_MS = 300;
/** Once shown, a loader stays at least this long so it reads as a state, not a flicker. */
const MIN_VISIBLE_MS = 500;

/**
 * Whether a loading indicator should be on screen — `isLoading`, without the
 * flash.
 *
 * A request that answers in 100 ms would otherwise draw a loader for one frame
 * and take it away again, which reads as the page blinking. So the indicator
 * waits `SHOW_AFTER_MS` before appearing, and once it has appeared it stays for
 * `MIN_VISIBLE_MS` even if the load finishes sooner. Fast loads show nothing;
 * slow ones show a loader that does not stutter.
 */
export function useDelayedLoading(isLoading: boolean): boolean {
  const [isShown, setIsShown] = useState(false);
  const shownAt = useRef(0);

  useEffect(() => {
    if (isLoading && !isShown) {
      const timer = setTimeout(() => {
        shownAt.current = Date.now();
        setIsShown(true);
      }, SHOW_AFTER_MS);
      return () => clearTimeout(timer);
    }

    if (!isLoading && isShown) {
      const remaining = MIN_VISIBLE_MS - (Date.now() - shownAt.current);
      const timer = setTimeout(() => setIsShown(false), Math.max(0, remaining));
      return () => clearTimeout(timer);
    }

    return undefined;
  }, [isLoading, isShown]);

  return isShown;
}
